import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";

import { useLaunchDarklyFlag } from "@lululemon/mwa-launch-darkly";
import { publicConfig } from "@lululemon/ecom-core";

import { useProductOutfitAvailability } from "../../../hooks/useProductOutfitAvailability";
import {
  useActiveColorInfo,
  useIsSoldOut,
  usePriceInfo,
  useActiveColorProductId,
  useActiveColorProductUnifiedId,
  useActiveColorProductOnSale,
} from "../../../hooks/use-product-attributes";
import logger from "../../outfitting/logger";
import { FM_EVENTS, trackFMEvent } from "../fm-analytics/fm-analytics";
import { reorderHatsProductsByCategory } from "../../../utils/reorder-products-by-category";

/**
 * This Hook is in charge of doing the request to the Findmine API.
 * @returns {*}
 */
export const useGetFindmineData = () => {
  const isSoldOut = useIsSoldOut();
  const productOnSale = useActiveColorProductOnSale();
  const productUnifiedId = useActiveColorProductUnifiedId();
  const productId = useActiveColorProductId();
  const priceInfo = usePriceInfo();
  const { listPrice, listPriceRange } = priceInfo;
  const price = listPriceRange?.[0] ?? listPrice;
  const { activeColor } = useActiveColorInfo();
  const previousColor = useRef(activeColor);
  const [productOutfitAvailability, setProductOutfitAvailability] =
    useProductOutfitAvailability();
  const { locale } = useRouter();

  const pdpOutfittingOnSaleItemsLDFlag = useLaunchDarklyFlag(
    "pdp-outfitting-on-sale-items",
    false,
    publicConfig?.launchDarkly?.clientSideId
  );
  const pdpEnableOutfittingLDFlag = useLaunchDarklyFlag(
    "pdp-enable-outfitting",
    false,
    publicConfig?.launchDarkly?.clientSideId
  );
  const [outfits, setOutfits] = useState([]);
  const [seedItem, setSeedItem] = useState();
  const [uuId, setUuId] = useState();
  const [loading, setLoading] = useState(false);

  /* The interpolation is used to avoid "Generic Object Injection Sink" es lint
   * error. */
  const applicationId =
    publicConfig?.findMineApplicationIds?.[`${locale}`] || null;
  const fetched = useRef(false);

  const reportError = (err, { fingerprint, tags = {} }) => {
    logger.error(err, {
      fingerprint: ["Outfitting", "Outfits-inspiration", fingerprint],
      tags: {
        componentName: "useGetFindmineData",
        featureName: "Outfitting:OutfitsInspiration",
        ...tags,
      },
    });
  };

  const doRequest = useCallback(async () => {
    setLoading(true);
    // Data needs to be reseted if a new request is done.
    setSeedItem(null);
    setOutfits(null);
    setUuId(null);
    try {
      const [language, region] = locale.split("-");
      const hostName = publicConfig.apiHost;
      const params = new URLSearchParams({
        application: applicationId,
        product_price: price,
        product_in_stock: !isSoldOut,
        product_on_sale: productOnSale,
        product_id: productUnifiedId?.replaceAll("-MD", ""),
        product_color_id: activeColor,
        return_pdp_item: true,
        region,
        language,
      });

      const response = await fetch(
        `${hostName}/outfitting/api/v3/complete-the-look?${params.toString()}`
      );
      const data = await response.json();
      const { looks = [], result, pdp_item, response_uuid, reason } = data;
      if (result === "success" && pdp_item && looks?.length > 0) {
        // Creating a product key to fill product outfit availability
        const productKey = `${productId}_${activeColor}`;
        const looksReorder = looks.map((look) => ({
          ...look,
          items: reorderHatsProductsByCategory(look?.items),
        }));
        setOutfits(looksReorder);
        setSeedItem(pdp_item);
        setUuId(response_uuid);
        setProductOutfitAvailability(productKey, true);
        //send tracking to findmine
        trackFMEvent(FM_EVENTS.PAGE_LOAD, locale, {
          response_uuid: response_uuid,
        });
      } else if (result === "success" && !pdp_item) {
        reportError(new Error("No Seed item found"), {
          fingerprint: "No-seed-item",
          tags: {
            functionName: "doRequest",
          },
        });
      } else if (result === "success" && (!looks || looks?.length === 0)) {
        reportError(new Error("No Looks to be displayed"), {
          fingerprint: "No-looks",
          tags: {
            functionName: "doRequest",
          },
        });
      } else {
        // It can happen that some are not registered in the findmine API.
        logger.info(new Error(reason), {
          fingerprint: [
            "Outfitting",
            "Outfits-inspiration",
            "Findmine-get-data",
            "No-outfits-for-item",
          ],
          tags: {
            componentName: "useGetFindmineData",
            functionName: "doRequest",
            featureName: "Outfitting:OutfitsInspiration",
          },
        });
      }
    } catch (err) {
      reportError(err, {
        fingerprint: "Findmine-get-data",
        tags: {
          functionName: "doRequest",
        },
      });
    } finally {
      setLoading(false);
    }
  }, [
    activeColor,
    applicationId,
    isSoldOut,
    locale,
    price,
    productId,
    productOnSale,
    productUnifiedId,
    setProductOutfitAvailability,
  ]);

  /**
   * IMPORTANT: Most of this logic is taken from:
   * packages/ecom-pdp/src/components/outfitting/outfitting.adapter.js
   * The original outfitting adapter, we want to keep displaying the component
   * under the same conditions, if the ABT for this component wins then the
   * original outfitting.adapter.js will be removed.
   */
  const isEnabled =
    !loading && (productOutfitAvailability || pdpEnableOutfittingLDFlag);
  const shouldShow =
    isEnabled &&
    outfits?.length > 0 &&
    (pdpOutfittingOnSaleItemsLDFlag || productOnSale !== "1");

  useEffect(() => {
    if (!fetched?.current) {
      doRequest();
      fetched.current = true;
    }
  }, [doRequest]);

  useEffect(() => {
    if (fetched?.current === true && previousColor.current !== activeColor) {
      previousColor.current = activeColor;
      doRequest();
    }
  }, [activeColor, doRequest]);

  return { outfits, seedItem, shouldShow, uuId, refetch: doRequest };
};

/**
 * This Hook is used for determining the previous index of a slide to animate transitions.
 * @returns {*}
 */

export const usePrevProps = (value) => {
  const ref = useRef();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
};
